import React, { useCallback, useEffect, useState } from 'react'
import PropTypes from 'prop-types'
import Select from 'react-select'
import { salesNameList } from 'domains/sales/deals/organisms/sales-name-select-search-field/sales-name-select-search-field'
import { Alert, Button, FormGroup, Input, Label, Modal, ModalBody, ModalFooter, ModalHeader } from 'reactstrap'
import OldDealsApi from 'api/v2/admins/inventory-old-deals-api-v2'
import { printInvoice } from 'utils/invoice-util'

export const canUseOldDeals = (user) => ['owner', 'admin', 'warehouse', 'web'].includes(String(user?.role || '').trim().toLowerCase()) ||
  String(user?.email || '').trim().toLowerCase() === 'sweta.sabilillah.belleandkate@gmail.com'

const errorMessage = (error) => error?.response?.data?.message || error?.message || 'Proses Old Deal gagal.'
const yesterdayWib = () => new Date(Date.now() + 7 * 60 * 60 * 1000 - 24 * 60 * 60 * 1000).toISOString().slice(0, 10)

const OldDealActions = ({ inventoryId, sku, onCreated }) => {
  const [options, setOptions] = useState(null)
  const [open, setOpen] = useState(false)
  const [busy, setBusy] = useState(false)
  const [printing, setPrinting] = useState(false)
  const [message, setMessage] = useState('')
  const [error, setError] = useState('')
  const [customers, setCustomers] = useState([])
  const [searching, setSearching] = useState(false)
  const [form, setForm] = useState({ customer_detail_id: '', customer_name: '', address: '', transaction_date: '', client_advisor: '', selling_price: '', confirm_completed_sale: false })

  const refresh = useCallback(async () => {
    try {
      const response = await OldDealsApi.options(inventoryId)
      setOptions(response.data)
    }
    catch (err) { setError(errorMessage(err)) }
  }, [inventoryId])

  useEffect(() => { refresh() }, [refresh])

  useEffect(() => {
    if (!open || form.customer_detail_id || form.customer_name.trim().length < 2) {
      setCustomers([])
      setSearching(false)
      return undefined
    }
    let active = true
    setSearching(true)
    const timer = setTimeout(() => {
      OldDealsApi.customers(form.customer_name.trim())
        .then((response) => { if (active) setCustomers(response.data.customers || []) })
        .catch((err) => { if (active) setError(errorMessage(err)) })
        .finally(() => { if (active) setSearching(false) })
    }, 350)
    return () => { active = false; clearTimeout(timer) }
  }, [open, form.customer_name, form.customer_detail_id])

  const setField = (key, value) => setForm((current) => ({ ...current, [key]: value }))
  const changeCustomerName = (event) => {
    const value = event.target.value
    setCustomers([])
    setForm((current) => ({ ...current, customer_name: value, customer_detail_id: '' }))
  }
  const startCreate = () => {
    setError('')
    setMessage('')
    setForm({ customer_detail_id: '', customer_name: '', address: '', transaction_date: '', client_advisor: '', selling_price: String(options?.selling_price || ''), confirm_completed_sale: false })
    setOpen(true)
  }
  const create = async () => {
    if (busy || !options?.can_create) return
    if (!form.customer_detail_id || !form.transaction_date || !form.client_advisor.trim() || !form.selling_price || !form.confirm_completed_sale) {
      setError('Pilih customer existing, isi tanggal, Client Advisor, harga transaksi, dan centang konfirmasi lunas.')
      return
    }
    setBusy(true)
    setError('')
    try {
      const response = await OldDealsApi.create(inventoryId, { ...form, sku })
      setMessage(response.data.message)
      setOpen(false)
      await refresh()
      onCreated()
    }
    catch (err) {
      setError(errorMessage(err))
      await refresh()
    }
    finally { setBusy(false) }
  }
  const print = async () => {
    if (printing || !options?.old_deal) return
    setPrinting(true)
    setError('')
    try {
      const response = await OldDealsApi.invoice(inventoryId)
      const result = await printInvoice(null, response.data.deal)
      setMessage(result?.warnings?.length ? 'Invoice diunduh; beberapa foto tidak tersedia. Periksa PDF sebelum dikirim ke customer.' : 'Old Invoice berhasil diunduh. SQ/IQ tetap 0/0.')
    }
    catch (err) { setError(errorMessage(err)) }
    finally { setPrinting(false) }
  }

  return (
    <div className='d-flex flex-column' style={{ maxWidth: '34rem', whiteSpace: 'normal' }}>
      <div className='d-flex flex-wrap' style={{ gap: '0.5rem' }}>
        <Button color='warning' disabled={busy || !options?.can_create} onClick={startCreate}>Create Old Deal</Button>
        <Button color='success' disabled={printing || !options?.old_deal} onClick={print}>{printing ? 'Preparing Invoice...' : 'Print Old Invoice'}</Button>
      </div>
      <small className='text-muted mt-1'>Untuk pencatatan transaksi lama yang sudah selesai/lunas dan belum punya Deal. Stok akhir tetap SQ 0 / IQ 0; tidak meminta settlement baru.</small>
      {options?.old_deal && <small className='mt-1'>Old Deal: {options.old_deal.reference_number} — {options.old_deal.transaction_date}</small>}
      {!options?.old_deal && options?.blockers?.map((reason) => <small className='text-danger d-block' key={reason}>{reason}</small>)}
      {message && <Alert color='success' className='mt-2 mb-0'>{message}</Alert>}
      {error && !open && <Alert color='danger' className='mt-2 mb-0' style={{ whiteSpace: 'pre-line' }}>{error}<Button size='sm' color='link' onClick={refresh}>Refresh</Button></Alert>}
      <Modal isOpen={open} toggle={() => { if (!busy) setOpen(false) }} size='lg'>
        <ModalHeader toggle={() => { if (!busy) setOpen(false) }}>Create Old Deal / Catch-up Invoice</ModalHeader>
        <ModalBody>
          <Alert color='info'>Hanya untuk SQ 0 / IQ 0 dan SKU yang belum pernah terhubung ke Deal atau Consignor Return (termasuk cancelled/completed). Riwayat Internal Request diperbolehkan; pastikan peminjaman/return internal sudah ditangani. Satu SKU, satu transaksi lama. Invoice memakai data customer existing.</Alert>
          {error && <Alert color='danger' style={{ whiteSpace: 'pre-line' }}>{error}</Alert>}
          <FormGroup>
            <Label>Customer name *</Label>
            <Input value={form.customer_name} maxLength={200} disabled={busy} placeholder='Cari nama lalu pilih detail customer' onChange={changeCustomerName} onKeyDown={(event) => { if (event.key === 'Enter') event.preventDefault() }} />
            {searching && <small>Mencari customer...</small>}
            {customers.map((customer) => <Button key={customer.id} color='link' className='d-block text-left' disabled={busy} onClick={() => setForm((current) => ({ ...current, customer_detail_id: customer.id, customer_name: customer.name }))}>{customer.name} — {customer.email} — {customer.phone} (Detail #{customer.id})</Button>)}
            {form.customer_detail_id && <small className='text-success'>Customer detail #{form.customer_detail_id} dipilih.</small>}
            {!searching && !form.customer_detail_id && form.customer_name.trim().length >= 2 && customers.length === 0 && <small>Nama tidak ditemukan. <a href='/app/sales/users' target='_blank' rel='noopener noreferrer'>Buka User List untuk menambahkan customer</a>, lalu cari ulang di sini.</small>}
          </FormGroup>
          <FormGroup><Label>Alamat (optional)</Label><Input type='textarea' value={form.address} maxLength={2000} disabled={busy} placeholder='Kosong = - pada invoice' onChange={(event) => setField('address', event.target.value)} /></FormGroup>
          <FormGroup><Label>SKU *</Label><Input value={sku} readOnly /></FormGroup>
          <FormGroup><Label>Tanggal transaksi lama *</Label><Input type='date' max={yesterdayWib()} value={form.transaction_date} disabled={busy} onChange={(event) => setField('transaction_date', event.target.value)} /></FormGroup>
          <FormGroup><Label>SA / Client Advisor *</Label><Select inputId='old-deal-client-advisor' options={salesNameList} isSearchable isClearable isDisabled={busy} value={salesNameList.find((option) => option.value === form.client_advisor) || null} onChange={(option) => setField('client_advisor', option?.value || '')} placeholder='Pilih SA / Client Advisor' /></FormGroup>
          <FormGroup><Label>Harga transaksi lama (IDR) *</Label><Input type='number' min='0.01' max='9999999999.99' step='0.01' value={form.selling_price} readOnly /><small>Mengikuti selling price produk saat ini dan tidak dapat diedit di form ini.</small></FormGroup>
          <FormGroup check><Label check><Input type='checkbox' checked={form.confirm_completed_sale} disabled={busy} onChange={(event) => setField('confirm_completed_sale', event.target.checked)} />Saya memastikan transaksi lama ini sudah selesai/lunas. Ini pencatatan ulang untuk invoice, bukan transaksi atau transfer baru.</Label></FormGroup>
        </ModalBody>
        <ModalFooter><Button color='secondary' disabled={busy} onClick={() => setOpen(false)}>Batal</Button><Button color='warning' disabled={busy || !options?.can_create || !form.customer_detail_id || !form.transaction_date || !form.client_advisor.trim() || !form.selling_price || !form.confirm_completed_sale} onClick={create}>{busy ? 'Membuat Old Deal...' : 'Create Old Deal'}</Button></ModalFooter>
      </Modal>
    </div>
  )
}

OldDealActions.propTypes = { inventoryId: PropTypes.oneOfType([PropTypes.number, PropTypes.string]).isRequired, sku: PropTypes.string.isRequired, onCreated: PropTypes.func.isRequired }
export default OldDealActions
